import { Check, Copy, Download, Search, Snowflake, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { Input } from "../../../../components/ui/input";
import { DownloadRequestButton } from "./components/download-request-button";
import { useErrorPix } from "./hooks/use-error-pix";
import { ErrorPixData } from "./types";
import {
  applyCnpjMask,
  cleanCnpj,
  formatDateBR,
} from "../../../../utils/format-utils";

const formatCpf = (v?: string | null) => {
  if (!v) return null;
  const d = v.replace(/\D/g, "");
  return d.length === 11
    ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
    : v;
};

const formatCnpj = (v?: string | null) => {
  if (!v) return null;
  const d = cleanCnpj(v);
  return d.length === 14 ? applyCnpjMask(d) : v;
};

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);

/** Grupo de erros PIX de uma mesma pessoa (mesmo documento) */
interface ErrorPixGroup {
  key: string;
  pessoa: ErrorPixData; // dados cadastrais (primeiro registro do grupo)
  erros: ErrorPixData[];
}

const CSV_HEADERS = [
  "Nome",
  "CPF",
  "CNPJ",
  "Telefone",
  "E-mail",
  "Chave PIX Cadastrada",
  "Tipo da Chave",
  "Sol ID",
  "OPS",
  "Retry",
  "Congelado",
  "Motivo do Erro",
  "Valor Face",
  "Valor Compra",
  "Data Romaneio",
  "Data Vencimento",
];

const csvCell = (value: string | null | undefined) =>
  `"${(value ?? "").replace(/"/g, '""')}"`;

/** Valor em centavos -> "175,12" (número que o Excel pt-BR já entende) */
const formatCurrencyCsv = (cents: number) =>
  (cents / 100).toFixed(2).replace(".", ",");

/**
 * Baixa os erros listados como CSV. Separador ";" e BOM UTF-8 porque é assim
 * que o Excel em pt-BR abre o arquivo sem juntar tudo numa coluna (a vírgula
 * já é o separador decimal) e sem quebrar os acentos dos nomes.
 * Retorna quantas linhas foram exportadas.
 */
const exportGroupsToCsv = (groups: ErrorPixGroup[]) => {
  const rows = groups.flatMap(({ pessoa, erros }) =>
    erros.map((e) => [
      pessoa.nomeCompleto,
      formatCpf(pessoa.cpf),
      formatCnpj(pessoa.cnpj),
      pessoa.telefone,
      pessoa.email,
      pessoa.chaveCadastro,
      pessoa.tipoChaveCadastro,
      e.sol_id,
      e.ops,
      e.allowRetryPayment ? "Sim" : "Não",
      e.freezed ? "Sim" : "Não",
      e.paymentMsg,
      formatCurrencyCsv(e.valorFace),
      formatCurrencyCsv(e.valorCompra),
      e.dataRomaneio ? formatDateBR(e.dataRomaneio) : "",
      e.dataVencimento ? formatDateBR(e.dataVencimento) : "",
    ])
  );

  const csv =
    "\uFEFF" +
    [CSV_HEADERS, ...rows].map((row) => row.map(csvCell).join(";")).join("\r\n");

  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8;" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `erros-pix-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);

  return rows.length;
};

export default function GestorErrorPix() {
  const {
    errorPixData,
    isLoading,
    toast,
    handleDownloadRequest,
    closeToast,
    showToast,
  } = useErrorPix();

  const [search, setSearch] = useState("");

  useEffect(() => {
    if (toast.open) {
      const timer = setTimeout(() => closeToast(), 6000);
      return () => clearTimeout(timer);
    }
  }, [toast, closeToast]);

  // Agrupa por documento (CNPJ > CPF > cnpjCpf): 5 erros da mesma pessoa
  // aparecem juntos, como um único cartão.
  // useMemo: o agrupamento não é recalculado quando o toast abre/fecha —
  // é isso que fazia a tabela "piscar" a cada clique no copy.
  const groups = useMemo<ErrorPixGroup[]>(() => {
    const byDoc = new Map<string, ErrorPixGroup>();
    errorPixData.forEach((item, index) => {
      const key = cleanCnpj(item.cnpj || "") || (item.cpf || "").replace(/\D/g, "") || item.cnpjCpf || `sem-doc-${index}`;
      const group = byDoc.get(key);
      if (group) {
        group.erros.push(item);
      } else {
        byDoc.set(key, { key, pessoa: item, erros: [item] });
      }
    });
    return Array.from(byDoc.values());
  }, [errorPixData]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((g) =>
      [
        g.pessoa.nomeCompleto,
        g.pessoa.cnpjCpf,
        g.pessoa.cpf,
        g.pessoa.cnpj,
        g.pessoa.telefone,
        g.pessoa.email,
        ...g.erros.map((e) => e.sol_id),
        ...g.erros.map((e) => e.ops),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [groups, search]);

  const copyGroupToClipboard = (group: ErrorPixGroup) => {
    const { pessoa, erros } = group;
    const chaveInfo = pessoa.chaveCadastro
      ? `${pessoa.chaveCadastro}${pessoa.tipoChaveCadastro ? ` (${pessoa.tipoChaveCadastro})` : ""}`
      : "não cadastrada";

    const cpfFmt = formatCpf(pessoa.cpf);
    const cnpjFmt = formatCnpj(pessoa.cnpj);

    const text = [
      `OPS: ${pessoa.ops || "—"}`,
      `Nome: ${pessoa.nomeCompleto || "—"}`,
      cpfFmt ? `CPF: ${cpfFmt}` : null,
      cnpjFmt ? `CNPJ: ${cnpjFmt}` : null,
      `Telefone: ${pessoa.telefone || "—"}`,
      `E-mail: ${pessoa.email || "—"}`,
      `Chave PIX cadastrada: ${chaveInfo}`,
      `Sol ID(s): ${erros.map((e) => e.sol_id).join(", ")}`,
      `Erros PIX (${erros.length}):`,
      ...erros.map(
        (e, i) =>
          `  ${i + 1}. Sol ID: ${e.sol_id} | Romaneio: ${e.dataRomaneio ? formatDateBR(e.dataRomaneio) : "—"} | Valor: ${formatCurrency(e.valorFace)} | Motivo: ${e.paymentMsg || "—"}`
      ),
    ]
      .filter(Boolean)
      .join("\n");

    navigator.clipboard
      .writeText(text)
      .then(() => showToast("Dados copiados para o clipboard!", "success"))
      .catch(() => showToast("Erro ao copiar.", "destructive"));
  };

  return (
    <div className="p-6">
      {toast.open && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <Card
            className={`${
              toast.variant === "destructive"
                ? "border-red-500 bg-red-50"
                : toast.variant === "success"
                  ? "border-green-500 bg-green-50"
                  : "border-blue-500 bg-blue-50"
            }`}
          >
            <CardContent className="p-4">
              <p className="text-sm font-medium">{toast.message}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Erros de Pix</h1>
          <p className="text-muted-foreground mt-1">
            Resolver PIX pendentes com erro
          </p>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">
                Buscando erros de PIX nos últimos 8 dias...
              </p>
            </div>
          </CardContent>
        </Card>
      ) : errorPixData.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Não há erros de PIX nos últimos 8 dias
              </h2>
              <p className="text-muted-foreground">
                Todos os pagamentos foram processados com sucesso
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Badge variant="destructive" className="text-sm">
                {errorPixData.length} erro(s) em {groups.length} pessoa(s)
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9 w-72"
                  placeholder="Buscar por nome, documento, sol_id, OPS..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Button
                variant="outline"
                className="gap-2"
                disabled={filteredGroups.length === 0}
                title="Baixar em CSV os erros listados (respeita a busca)"
                onClick={() => {
                  const linhas = exportGroupsToCsv(filteredGroups);
                  showToast(`CSV baixado com ${linhas} erro(s).`, "success");
                }}
              >
                <Download className="h-4 w-4" />
                Baixar CSV
              </Button>
              <DownloadRequestButton
                onConfirm={handleDownloadRequest}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-4">
            {filteredGroups.map((group) => {
              const { pessoa, erros } = group;
              const cpfFmt = formatCpf(pessoa.cpf);
              const cnpjFmt = formatCnpj(pessoa.cnpj);

              return (
                <Card key={group.key} className="border-l-4 border-l-red-400 overflow-hidden">
                  {/* Cabeçalho da pessoa — colunas de largura fixa para os campos
                      ficarem alinhados de um card para o outro, já que nome, CPF,
                      e-mail e chave PIX têm comprimentos bem diferentes. */}
                  <div className="flex items-center gap-4 border-b bg-muted/40 px-4 py-3">
                    <div className="grid flex-1 grid-cols-1 gap-x-4 gap-y-2 lg:grid-cols-[minmax(12rem,1.4fr)_11rem_13rem_9rem_minmax(10rem,1.2fr)_minmax(9rem,1fr)]">
                      <span className="truncate font-semibold" title={pessoa.nomeCompleto || undefined}>
                        {pessoa.nomeCompleto || <span className="text-muted-foreground">Sem nome</span>}
                      </span>

                      <span className="flex items-center gap-1 text-sm">
                        {cpfFmt ? (
                          <>
                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-[0.6rem] px-1 py-0">
                              CPF
                            </Badge>
                            {cpfFmt}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </span>

                      <span className="flex items-center gap-1 text-sm">
                        {cnpjFmt ? (
                          <>
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[0.6rem] px-1 py-0">
                              CNPJ
                            </Badge>
                            {cnpjFmt}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </span>

                      <span className="truncate text-sm text-muted-foreground">
                        {pessoa.telefone || "—"}
                      </span>

                      <span className="truncate text-sm text-muted-foreground" title={pessoa.email || undefined}>
                        {pessoa.email || "—"}
                      </span>

                      <span className="flex items-center gap-1 truncate text-sm" title={pessoa.chaveCadastro || undefined}>
                        <span className="shrink-0 text-muted-foreground">PIX:</span>
                        {pessoa.chaveCadastro ? (
                          <>
                            <span className="truncate">{pessoa.chaveCadastro}</span>
                            {pessoa.tipoChaveCadastro && (
                              <Badge variant="secondary" className="shrink-0 text-[0.65rem] px-1 py-0">
                                {pessoa.tipoChaveCadastro}
                              </Badge>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">não cadastrada</span>
                        )}
                      </span>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant={erros.length > 1 ? "destructive" : "outline"} className="text-xs">
                        {erros.length} erro(s)
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Copiar dados da pessoa com todos os sol_ids"
                        onClick={() => copyGroupToClipboard(group)}
                      >
                        <Copy className="mr-1.5 h-4 w-4 text-primary" />
                        Copiar
                      </Button>
                    </div>
                  </div>

                  {/* Erros da pessoa */}
                  <div className="overflow-x-auto">
                    {/* table-fixed + larguras explícitas: cada card tem a sua própria
                        <table>, então com layout automático cada uma media as colunas
                        pelo próprio conteúdo (um motivo de erro longo empurrava Retry e
                        Congelado) e os cards saíam desalinhados entre si. Só o Motivo
                        fica flexível, absorvendo a sobra. */}
                    <table className="w-full min-w-[86rem] table-fixed text-sm">
                      <thead className="bg-muted/20 text-left text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="w-64 px-4 py-2 font-medium">Sol ID</th>
                          <th className="w-32 px-4 py-2 font-medium">OPS</th>
                          <th className="w-20 px-4 py-2 font-medium">Retry</th>
                          <th className="w-28 px-4 py-2 font-medium">Congelado</th>
                          <th className="px-4 py-2 font-medium">Motivo do Erro</th>
                          <th className="w-28 px-4 py-2 font-medium">Valor Face</th>
                          <th className="w-32 px-4 py-2 font-medium">Valor Compra</th>
                          <th className="w-36 px-4 py-2 font-medium">Data Romaneio</th>
                          <th className="w-40 px-4 py-2 font-medium">Data Vencimento</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {erros.map((e, i) => (
                          <tr key={e.id || `${group.key}-${i}`}>
                            <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{e.sol_id}</td>
                            <td className="px-4 py-2 truncate" title={e.ops}>{e.ops}</td>
                            <td className="px-4 py-2">
                              {e.allowRetryPayment ? (
                                <Check className="h-5 w-5 text-green-500" />
                              ) : (
                                <X className="h-5 w-5 text-muted-foreground" />
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {e.freezed ? <Snowflake className="h-5 w-5 text-blue-400" /> : null}
                            </td>
                            <td className="px-4 py-2">
                              {e.paymentMsg ? (
                                <span className="text-red-600 font-semibold" title={e.paymentMsg}>
                                  {e.paymentMsg}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">{formatCurrency(e.valorFace)}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{formatCurrency(e.valorCompra)}</td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {e.dataRomaneio ? formatDateBR(e.dataRomaneio) : "-"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {e.dataVencimento ? formatDateBR(e.dataVencimento) : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              );
            })}

            {filteredGroups.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Nenhum resultado para “{search}”.
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
